// =============================================================
// TermsConditions — "Legal document" layout
// -------------------------------------------------------------
// Sticky numbered table of contents + numbered section heads on
// cream paper. Shares the shell primitives in
// components/legal/DocShell with the Cookie Policy page.
//
// The `termsSections` data array carries the Portuguese copy
// (v08072026 legal doc) as title / paragraphs / subsections /
// bullets / closing. Section #3 carries a shortened `tocLabel`.
// =============================================================

import React from 'react';

import Header from 'components/Header';
import Footer from 'components/Footer';

import {
  DocHero,
  Toc,
  DocShell,
  DocSection,
  IntroNote,
  Subsection,
  DocList,
  ClosingNote,
  P,
} from 'components/legal/DocShell';

// ------------------------------------------------------------------
// DATA — Portuguese copy, unchanged
// ------------------------------------------------------------------
const linkClass =
  'font-semibold text-yellow-04 underline decoration-yellow-04/40 underline-offset-2 hover:decoration-yellow-04';

const termsSections = [
  {
    title: 'Introdução',
    paragraphs: [
      <>
        A There2b é uma plataforma online disponível no uniform resource locator (URL){' '}
        <a href="https://www.there2b.com" target="_blank" rel="noopener noreferrer" className={linkClass}>
          www.there2b.com
        </a>{' '}
        (a "There2b"), que funciona como um marketplace de espaços comerciais (desde feiras e
        mercados, a lojas como concept e pop-up stores), a disponibilizar por promotores (os
        "Promotores") a quaisquer pessoas singulares ou coletivas com interesse na aquisição de um
        direito de utilização sobre esses espaços (as "Marcas").
      </>,
      'Promotores e Marcas serão doravante conjuntamente designados por "Utilizadores" ou, de forma individual, por "Utilizador".',
      'O registo, acesso e utilização da There2b pelos Utilizadores são regulados por estes Termos e Condições.',
      'O incumprimento de uma ou mais disposições previstas nestes Termos e Condições, insanável ou não sanado num prazo razoável para o efeito, a conceder pela There2b aos Utilizadores, acarreta a suspensão imediata do acesso, registo e utilização da There2b, sem prejuízo das consequências legais emergentes dos factos em causa.',
    ],
  },
  {
    title: 'Serviços prestados pela There2b',
    paragraphs: [
      'Conforme referido no início destes Termos e Condições, a There2b é uma plataforma online que funciona como um marketplace de espaços comerciais (desde feiras e mercados, a lojas como concept e pop-up stores), a disponibilizar por promotores (os "Promotores"), a quaisquer pessoas singulares ou coletivas com interesse na aquisição de um direito de utilização sobre esses espaços (as "Marcas").',
      'Os serviços prestados pela There2b restringem-se, exclusivamente, ao registo, acesso e uso da There2b, não abrangendo a prestação de serviços relacionados com a disponibilização e gestão dos espaços comerciais, que são da exclusiva responsabilidade dos Promotores.',
      'Os serviços prestados pela There2b serão doravante designados por "Serviços".',
    ],
  },
  {
    title: 'Termos e condições do funcionamento da plataforma There2b',
    tocLabel: 'Funcionamento da plataforma',
    subsections: [
      {
        title: 'Registo',
        paragraphs: [
          'Sem prejuízo das normas de segurança implementadas pela There2b ao nível dos sistemas de informação e da cibersegurança, o Utilizador é responsável pela integridade da sua conta pessoal, incluindo os dados de acesso à mesma.',
          'Para se registar e utilizar a There2b, o Utilizador deverá fornecer, com verdade, um conjunto de informações (podendo, ou não, incluir documentos). A There2b reserva-se o direito de, unilateralmente, eliminar o registo de qualquer Utilizador que tenha submetido informação ou documentos falsos ou incompletos, ou caso constate haver indício de fraude no processo de criação do registo.',
          'A conta pessoal criada pelo Utilizador é intransmissível e apenas pode ser utilizada pelo Utilizador.',
          'O Utilizador é responsável pela exatidão das informações fornecidas na altura do registo e compromete-se a manter o seu perfil atualizado enquanto o registo se mantiver ativo.',
          <>
            Para além do registo na There2b, o Promotor deverá ainda registar-se na plataforma da{' '}
            <a href="https://www.easypay.pt" target="_blank" rel="noopener noreferrer" className={linkClass}>
              easypay
            </a>{' '}
            (www.easypay.pt), instituição de pagamento habilitada para o efeito, por ser esta a
            receber os valores pagos pelas Marcas e a assegurar os respetivos repasses para a conta
            bancária do Promotor após a realização do evento. A intervenção da easypay é essencial
            para garantir a conformidade legal da operação, ao abrigo da legislação aplicável em
            matéria de serviços de pagamento, bem como o rigor e a segurança de todos os pagamentos
            e repasses.
          </>,
        ],
      },
      {
        title: 'Criação de eventos pelos Promotores',
        paragraphs: [
          'Os Promotores podem criar e publicar na There2b um número ilimitado de eventos, no entanto, a There2b reserva-se o direito de não aceitar a criação e/ou publicação de eventos em caso de não verificação dos seguintes requisitos cumulativos:',
        ],
        bullets: [
          'A natureza e características do evento permitem a comercialização de espaços comerciais através da There2b, como, por exemplo, feiras e mercados, lojas do tipo concept e pop-up, entre outros;',
          'Os eventos são reais e a sua realização está prevista acontecer, sem prejuízo das situações de cancelamento abaixo descritas;',
          'O Promotor é uma pessoa singular, maior de idade, ou uma pessoa coletiva legalmente constituída e habilitada a exercer a atividade de Promotor;',
          'Cumprimento integral e pontual destes Termos e Condições pelo Promotor.',
        ],
      },
      {
        title: 'Inscrição em eventos pelas Marcas',
        paragraphs: [
          'Depois de registadas na There2b, as Marcas podem inscrever-se em quaisquer eventos criados e publicados por Promotores.',
          'Os Promotores podem, a seu livre critério, rejeitar a inscrição de uma ou mais Marcas.',
          'Sem prejuízo do disposto no parágrafo anterior, a inscrição nos eventos poderá ser desconsiderada pela There2b em caso de não pagamento do preço da inscrição, incluindo a comissão da There2b, ou caso a Marca não tenha submetido a informação e documentos solicitados ou, tendo-o, o tenha feito de forma incompleta e, ainda, caso haja indícios de fraude no processo de inscrição.',
        ],
      },
      {
        title: 'Pagamentos e faturação',
        paragraphs: [
          'O preço de inscrição no evento é pago no fim do processo de inscrição.',
          'Nesse preço, está incluída a comissão da There2b.',
          'A comissão da There2b, que é cobrada à Marca, corresponde a 5% (cinco por cento) sobre o preço de inscrição. Atualmente, a There2b não cobra qualquer comissão ou remuneração ao Promotor.',
          'Os valores pagos pelas Marcas são recebidos pela easypay, que assegura o respetivo processamento e o repasse ao Promotor. No prazo de 48 horas após a data de realização do evento (e, no caso de eventos com vários dias, da data do último dia do evento), a easypay repassa ao Promotor o preço de inscrição do evento, deduzido da comissão da There2b, que esta retém para si.',
          'No que diz respeito à faturação:',
        ],
        bullets: [
          'A There2b emite fatura à Marca pelo total do valor da comissão que esta haja pago no processo de inscrição no evento, em contrapartida pela prestação dos Serviços;',
          'O Promotor emite fatura à Marca pelo valor do serviço prestado pelo Promotor (i.e., preço do evento).',
        ],
        closing:
          'Aos pagamentos referidos nos parágrafos anteriores acresce IVA à taxa legal aplicável.',
      },
      {
        title: 'Alterações e cancelamentos pelos Promotores',
        paragraphs: [
          'Os Promotores podem submeter à apreciação da There2b alterações ao evento, bem como, assim, solicitar o seu cancelamento.',
          'Em caso de cancelamento, a Marca tem sempre direito ao reembolso total do preço do evento e da comissão da There2b.',
          'Caso o cancelamento do evento não seja motivado por justa causa, o Promotor poderá ainda ficar obrigado ao pagamento de uma taxa de cancelamento à There2b.',
        ],
      },
      {
        title: 'Alterações e cancelamentos pelas Marcas',
        paragraphs: [
          <>
            As Marcas podem solicitar alterações à sua reserva num determinado evento. Para o efeito,
            deverão enviar um email para{' '}
            <a href="mailto:geral@there2b.com" className={linkClass}>
              geral@there2b.com
            </a>
            . O Promotor pode ou não aceitar essas alterações, em função da disponibilidade dos
            espaços, características do evento, condições e termos previstos no regulamento do evento,
            se existente, etc.
          </>,
          <>
            As Marcas podem também cancelar a sua inscrição num determinado evento. Os pedidos de
            cancelamento deverão ser formulados e submetidos por email, para o endereço{' '}
            <a href="mailto:geral@there2b.com" className={linkClass}>
              geral@there2b.com
            </a>
            . As condições do cancelamento, incluindo o eventual direito ao reembolso do preço de
            inscrição, são definidas pelo Promotor para cada evento e podem ser consultadas na página
            do evento. Quanto à comissão da There2b, esta reserva-se sempre o direito de não
            reembolsar o respetivo valor, considerando que o Serviço já foi prestado e que a There2b
            já assumiu os custos e encargos associados à inscrição e ao processamento dos pagamentos.
          </>,
        ],
      },
    ],
  },
  {
    title: 'Dados pessoais',
    paragraphs: [
      'A There2b é responsável pelo tratamento dos dados pessoais dos Utilizadores recolhidos no âmbito do registo na There2b. A responsabilidade da There2b não prejudica a responsabilidade direta dos Promotores, também na qualidade de responsáveis pelo tratamento, pelos dados pessoais das Marcas que recolha e trate no âmbito do processo de inscrição em eventos.',
      'No que se refere à responsabilidade da There2b, esta limita-se às operações de tratamento relacionadas com a gestão e manutenção da There2b.',
      'As operações de tratamento conduzidas pela There2b são-no com respeito pelas finalidades associadas ao respetivo tratamento e em cumprimento das obrigações previstas na legislação aplicável à proteção de dados, designadamente, o Regulamento (UE) 2016/679 do Parlamento Europeu e do Conselho de 27 de abril de 2016 e a Lei n.º 58/2019, de 08 de agosto.',
      'Para medir a audiência da There2b e melhorar o serviço, a There2b regista, de forma agregada, as páginas visitadas e algumas ações realizadas na plataforma (por exemplo, abrir a página de um evento ou clicar para reservar um espaço). Esta medição é feita pela própria There2b, sem cookies e sem recurso a serviços de terceiros. O endereço IP não é guardado: a partir dele e da identificação do navegador é calculado um código pseudonimizado que muda todos os dias, o que impede reconhecer o mesmo visitante em dias diferentes. São ainda registados o domínio do site de onde o visitante chegou, o tipo de dispositivo (computador, telemóvel ou tablet) e, quando existam, os parâmetros de campanha do endereço (utm). Estes dados destinam-se exclusivamente a estatísticas internas, têm como fundamento o interesse legítimo da There2b em conhecer e melhorar a utilização da plataforma, não são partilhados com terceiros e são conservados apenas pelo período necessário a essa finalidade.',
      'Para conhecer os termos aplicáveis às operações de tratamento da responsabilidade da There2b (incluindo, finalidades e fundamentos jurídicos, prazos de conservação, transmissão de dados, direitos dos Clientes, etc.), os Utilizadores poderão consultar a política de privacidade da There2b disponível no site.',
    ],
  },
  {
    title: 'Alterações aos Termos e Condições',
    paragraphs: [
      'A There2b reserva-se o direito de, a todo o tempo, alterar o conteúdo dos Termos e Condições.',
      'As alterações que venham a ser introduzidas só entrarão em vigor após terem sido devidamente divulgadas junto dos Utilizadores e por estes aceites, expressa ou tacitamente.',
      'A manutenção do registo dos Utilizadores na There2b, após divulgação das alterações, é tida, para todos os efeitos, como aceitação tácita das mesmas.',
    ],
  },
  {
    title: 'Incumprimento',
    paragraphs: [
      'O incumprimento de uma ou mais disposições previstas nestes Termos e Condições, insanável ou não sanado num prazo razoável para o efeito, a conceder pela There2b ao Utilizador, acarreta a suspensão imediata do acesso, registo e utilização da There2b, sem prejuízo das consequências legais emergentes dos factos em causa.',
    ],
  },
  {
    title: 'Lei e Foro',
    paragraphs: [
      'O presente Acordo será regulado pela lei portuguesa.',
      'Qualquer litígio emergente do presente Acordo será dirimido pelos Tribunais da Comarca de Lisboa, com expressa renúncia à competência de qualquer outro foro.',
    ],
  },
];

// give each section a stable id once
const sections = termsSections.map((s, i) => ({ ...s, id: `t${i + 1}` }));

const TermsConditionsPage = () => {
  const tocItems = sections.map((s) => ({ id: s.id, label: s.tocLabel || s.title }));

  return (
    <div className="min-h-screen flex flex-col bg-paper">
      <Header />

      <main className="flex-1">
        <DocHero title="Termos e Condições de Utilização" />

        <DocShell toc={<Toc items={tocItems} />}>
          <IntroNote>
            Estes Termos e Condições regulam o registo, o acesso e a utilização da plataforma
            There2b por promotores e marcas. Ao utilizar a plataforma, o Utilizador aceita-os
            integralmente.
          </IntroNote>

          {sections.map((section, i) => (
            <DocSection key={section.id} id={section.id} index={i + 1} title={section.title}>
              {section.paragraphs?.map((p, idx) => (
                <P key={idx}>{p}</P>
              ))}

              {section.subsections?.map((sub) => (
                <Subsection key={sub.title} title={sub.title}>
                  {sub.paragraphs?.map((p, idx) => (
                    <P key={idx}>{p}</P>
                  ))}
                  {sub.bullets?.length ? <DocList items={sub.bullets} /> : null}
                  {sub.closing ? <ClosingNote>{sub.closing}</ClosingNote> : null}
                </Subsection>
              ))}
            </DocSection>
          ))}
        </DocShell>
      </main>

      <Footer />
    </div>
  );
};

export default TermsConditionsPage;
